import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  ActivityEntry,
  ActivityFeedResponse,
  ActivityGroup,
  ActivityQuery,
  fetchActivity,
} from '../../api/activity-client';
import {
  duration,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
} from '../../style-utils/theme';
import { down, touch } from '../../style-utils/media-queries';
import { shimmerLoop, skeletonBackground, spin } from '../../style-utils/motion';
import {
  ActiveIndicator,
  Collapse,
  IndicatorGroup,
  StateSwitch,
  Staleable,
} from '../../motion';
import { Text, font } from '../../style-utils/text';
import { ActivityEntryRow, FilterToggle, List } from './activity-entry-row';
import { FormModal } from '../modal/form-modal';
import { RecordDetail } from './record-detail';
import { addDays, todayIn } from '../../function-utils/building-time';
import { useClock } from '../../context/clock-context';
import { ClockNote } from '../clock-note/clock-note';

// THE ACTIVITY FEED. One component, two surfaces.
//
// The desk gets `compact` — a glance at what has happened, grouped the way the
// desk thinks about it. An admin gets the same list with the filter row opened
// and a building column. Two components would drift, and the one that drifts is
// always the one nobody is looking at.
//
// PROGRESSIVE DISCLOSURE, per the brief: the group tabs are always visible
// because they are how you navigate; everything else lives behind "Filters" and
// stays out of the way until asked for.
//
// THE GROUPING IS THE SERVER'S. `group` and `groupLabel` arrive on every entry
// (activity/feed.js) rather than being derived here from `source`, because the
// derivation is not obvious — a resident coming home and a guest arriving are
// both visitor_log rows and the desk keeps two different books.

const Panel = styled.section<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  width: 100%;
  /* A feed is full of names and free text. Without this a single long word sets
     the width of the whole column and the PAGE scrolls sideways, which on iOS
     also breaks the scroll-to-top tap. */
  min-width: 0;
  overflow-wrap: anywhere;
  ${({ compact }) => (compact ? '' : 'min-height: 320px;')}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

/* The title and the controls beside it. The search box used to be a full-width
   bar on its own line under this row: at desk width that is a ~700px input to
   type a four-letter name into, and it read as the most important thing on the
   panel when it is a way of narrowing one. It is bounded and sits inline now —
   still outside the filter disclosure, because search is how you find one thing
   you already have in mind and burying it makes the feed feel like a wall. */
const HeaderTools = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex: 0 1 auto;
  min-width: 0;
`;

const Tabs = styled.div`
  display: flex;
  gap: ${spacing.xs};
  flex-wrap: wrap;

  /* Wrapped by default; on a narrow phone wrapping four tabs costs three lines,
     so they scroll horizontally instead. -webkit-overflow-scrolling keeps the
     momentum feel on iOS Safari. */
  ${down('sm')} {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: ${spacing.half};

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

/* THE ACTIVE FILL IS NOT PAINTED BY THE TAB. It is TabPill below — one element
   that MOVES from the previous tab to the next (framer layoutId) instead of a
   background redrawn on each button. The tab keeps only its resting hairline
   (transparent while active, so the pill's own border is the one you see) and
   its text colour, which still cross-fades at the colour tempo. */
const Tab = styled.button<{ active?: boolean }>`
  position: relative;
  flex: 0 0 auto;
  white-space: nowrap;
  border: 1px solid ${({ active }) => (active ? 'transparent' : palette.line)};
  background: transparent;
  color: ${({ active }) => (active ? palette.irisDeep : palette.slate)};
  font: inherit;
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: ${transition.colors};

  ${touch} {
    min-height: 44px;
    padding: ${spacing.xs} ${spacing.sm};
  }

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
  }
`;

/* The pill under the active tab: inset -1px so it sits exactly on the tab's
   border box, under the label. Rendered inside the active tab only; framer
   carries it to the next tab with the indicator spring. */
const TabPill = styled(ActiveIndicator)`
  position: absolute;
  inset: -1px;
  border-radius: ${radius.pill};
  background: ${palette.irisTint};
  border: 1px solid ${palette.iris};
  z-index: 0;
`;

const TabLabel = styled.span`
  position: relative;
  z-index: 1;
`;

const Filters = styled.div`
  display: flex;
  gap: ${spacing.xs};
  flex-wrap: wrap;
  align-items: flex-end;
  padding: ${spacing.sm};
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
`;

/* A labelled control. The filter row was six bare inputs whose placeholders
   vanished the moment anything was typed into them, so a narrowed feed gave no
   account of WHY it was narrowed — the panel above just had fewer rows in it.
   The two date fields make that unmissable: a bare date input reads as neither
   an end of a range nor a beginning. */
const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  flex: 1 1 140px;
  min-width: 0;

  > span {
    font-size: ${typography.size.xxs}px;
    font-weight: ${typography.weight.semibold};
    letter-spacing: ${typography.tracking.caps};
    text-transform: uppercase;
    color: ${palette.slateLight};
  }
`;

const Field = styled.input`
  font: inherit;
  font-size: ${typography.size.xs}px;
  padding: ${spacing.xs};
  /* 13px of text inside 8px of padding lands at 42px — two short of the floor,
     which is the kind of miss no one sees and everyone feels. */
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  min-width: 0;
  width: 100%;
  transition: ${transition.colors};

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
  /* iOS Safari ZOOMS THE WHOLE PAGE when a focused field is under 16px, and it
     does not zoom back out. 16px on coarse pointers only, so the dense desk
     layout is unchanged at a mouse. */
  ${touch} {
    font-size: 16px;
  }
`;

const Select = styled.select`
  font: inherit;
  font-size: ${typography.size.xs}px;
  padding: ${spacing.xs};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  width: 100%;
  min-width: 0;
  transition: ${transition.colors};

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
  /* Same iOS zoom rule as Field above — a <select> triggers it too. */
  ${touch} {
    font-size: 16px;
  }
`;

// THE BUILDING'S DASHBOARD, in one line. Owner: the main menu should carry the
// dashboard for the building directly, not a link to it.
const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;

  ${down('sm')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

/**
 * ═══════════ THE TILES ARE CONTROLS ═══════════
 *
 * Owner report, 2026-08-14: "these are not respected as filters". They were
 * <div>s — four numbers describing a narrowing that the reader then had to go
 * and perform by hand in the filter drawer, on a panel whose whole argument is
 * that the answer is already on screen. Pressing "Still inside" is the obvious
 * gesture and it did nothing.
 *
 * A <button>, not a div with onClick: this is a real control, so it needs the
 * keyboard, the focus ring and the role for free rather than three attributes
 * that approximate them.
 *
 * `$active` is the pressed state and it is LOUD on purpose. A feed narrowed to
 * one direction looks exactly like a quiet day — the failure mode the whole
 * "0 on everything" report was about — so the tile that caused it stays lit,
 * and pressing it again clears it.
 */
const Stat = styled.button<{ alert?: boolean; $active?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border: 1px solid
    ${({ alert, $active }) =>
      $active ? palette.iris : alert ? palette.warning : palette.line};
  background: ${({ alert, $active }) =>
    $active ? palette.irisTint : alert ? palette.warningSoft : 'transparent'};
  border-radius: 8px;
  padding: 8px 16px;
  min-width: 0;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${({ $active }) => ($active ? palette.iris : palette.lineStrong)};
    background: ${({ alert, $active }) =>
      $active ? palette.irisTint : alert ? palette.warningSoft : palette.irisTintSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  b {
    display: block;
    font-size: ${typography.size.xl}px;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: ${({ alert, $active }) =>
      $active ? palette.irisDeep : alert ? palette.warning : palette.ink};
  }

  span {
    display: block;
    margin-top: 4px;
    font-size: ${typography.size.xxs}px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ $active }) => ($active ? palette.irisDeep : palette.slateLight)};
  }

  ${touch} {
    min-height: 64px;
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 16px 4px;
  color: ${palette.slateLight};
  font-size: 13px;
`;

/* The reset. A link, not a button: it undoes a state rather than performing an
   act, and it must not compete with the controls above it. */
const ClearFilters = styled.button`
  align-self: center;
  border: none;
  background: none;
  padding: ${spacing.xs} 0;
  font: inherit;
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  cursor: pointer;
  flex: 0 0 auto;

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
  }
`;

/* "Showing 25 of 118". The feed grows a page at a time and nothing said how far
   through it you were, so "Show more" was a button with no idea behind it — and
   when it stopped appearing there was no way to tell "that is everything" from
   "it broke". Paired with the button, and rendered even when the button is gone
   so the end of a list is stated rather than implied. */
const PageNote = styled.p`
  margin: 0;
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

/* Plan 3 F20 (b): one line under the filters, full width of the row. */
export const DESK_DAY_NOTE =
  'Each day here is a desk day: it runs from 07:00 to 07:00 in the building’s time.';

const DayNote = styled.p`
  flex-basis: 100%;
  margin: 0;
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

const More = styled.button`
  align-self: flex-start;
  border: 1px solid ${palette.lineStrong};
  background: ${palette.surface};
  font: inherit;
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
    color: ${palette.irisDeep};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: 44px;
  }
`;

/* SEARCH, BOUNDED. Wide enough for a name and a word, never the whole panel. */
const SearchField = styled(Field)`
  flex: 0 1 240px;
  width: 240px;
  max-width: 100%;

  ${down('sm')} {
    flex: 1 1 100%;
    width: 100%;
  }
`;

/* ── LOADING, IN THE TWO SHAPES IT ACTUALLY TAKES ──────────────────────────
 *
 * The panel had `aria-busy` and one line of text reading "Loading…", so a
 * screen reader was told and a person was not: a filter change or a page load
 * left the feed sitting there looking finished but wrong.
 *
 * FIRST LOAD gets SKELETON ROWS, per design_system §7: "shimmer skeletons
 * shaped like the content (not a full-screen spinner)". Shaped like a row —
 * time, body, action — so the layout does not jump when the real rows land.
 *
 * A REFETCH gets the inline SPINNER beside the title instead. The rows already
 * on screen are still true; replacing them with skeletons would throw away
 * something readable to announce that something readable is coming. The list
 * dims slightly so it reads as stale rather than current.
 */
const SkeletonBar = styled.span<{ $w: string }>`
  display: block;
  width: ${({ $w }) => $w};
  height: 10px;
  border-radius: ${radius.xs};
  ${skeletonBackground}
  ${shimmerLoop}
`;

const SkeletonRow = styled.li`
  display: grid;
  grid-template-columns: 64px 1fr auto;
  gap: ${spacing.sm};
  align-items: center;
  padding: ${spacing.sm} 4px;
  border-top: 1px solid ${palette.line};

  &:first-of-type {
    border-top: none;
  }

  ${down('sm')} {
    grid-template-columns: 52px 1fr;
  }
`;

/* Inline, and only inline — the design system reserves the spinner for exactly
   this: a control or a header saying "still working", never a whole screen. */
const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid ${palette.line};
  border-top-color: ${palette.lagoon};
  border-radius: ${radius.circle};
  animation: ${spin} ${duration.slow} linear infinite;
  flex: 0 0 auto;
`;

/* THE FEED'S ONE BOX. Skeleton, error, empty and the list are four fills of
   the same box (StateSwitch crossfades them, mode wait, so two never stack),
   and the wrapper re-applies the panel's column gap inside the switch so the
   list and its footer keep their spacing. The refetch answer is the shared
   Staleable: the rows stay, dim to OPACITY.refreshing, and come back — what
   is on screen is still the last true answer, and blanking it would be a
   worse lie than showing it a moment stale. */
const FeedSwitch = styled(StateSwitch)`
  > div {
    display: flex;
    flex-direction: column;
    gap: ${spacing.sm};
  }
`;

const PAGE = 25;

export interface ActivityFeedProps {
  buildingId?: number | null;
  /** The desk glance: fewer rows, filters collapsed, no building column. */
  compact?: boolean;
  title?: string;
  /** Show the counts strip — the desk's at-a-glance dashboard for this
   *  building. Off on the admin view, which has its own analytics above. */
  showSummary?: boolean;
  /** HOW FAR BACK THIS SURFACE OPENS, in days. The server's own default is 7,
   *  which is right for a desk glance and wrong for a reporting screen that
   *  should open showing everything there is. Expressed as a starting
   *  dateFrom rather than a second server default, so the reader can see the
   *  window it opened on and change it with the same control they would use
   *  anyway. */
  defaultDays?: number;
  /** Opens the record behind a row. Omitted on surfaces that have nowhere to go
   *  yet — the row then simply does not offer the affordance, rather than
   *  offering one that goes nowhere. */
  onOpen?: (entry: ActivityEntry) => void;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  buildingId = null,
  compact = false,
  title = 'Recent activity',
  showSummary = false,
  defaultDays,
  onOpen,
}) => {
  const [data, setData] = useState<ActivityFeedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState<ActivityGroup | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [limit, setLimit] = useState(compact ? 8 : PAGE);
  const [unit, setUnit] = useState('');
  const [person, setPerson] = useState('');
  const [status, setStatus] = useState('');
  const [direction, setDirection] = useState('');
  const [day, setDay] = useState('');
  /* WHOSE CLOCK THIS FEED IS ON (R-T12) — the surface it is drawn on decides
     first: at the desk every time is the BUILDING's, for an owner too; anywhere
     else it is the clock the response carries. One value for the default
     window, every row and the note in the footer, so none can disagree. */
  const clock = useClock({
    buildingId,
    buildingZone: data?.entries?.find(entry => entry.tz)?.tz,
    response: data,
  });
  // Seeded from defaultDays when the surface asks for a wider window. A
  // STATE seed rather than a query override, so the From box shows the date
  // the screen opened on and the reader can move it. Counted back from TODAY ON
  // THE FEED'S CLOCK, by calendar days — it was a UTC slice of the browser's
  // clock, which is yesterday's date for a desk west of Greenwich in the evening.
  const [dateFrom, setDateFrom] = useState(() =>
    defaultDays ? addDays(todayIn(clock.zone), -defaultDays) : ''
  );
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  // ---- "OPEN" RAISES THE RECORD OVER THE LIST ----
  //
  // It used to navigate to /record/:source/:id — a whole page change to read
  // four fields, and coming back re-mounted the feed, so the filters, the search
  // term and the page you had grown were all gone. Owner instruction,
  // 2026-08-13.
  //
  // The deep-link ROUTE is untouched and still exists: a record has to be
  // pasteable to a colleague and survive a refresh. Same <RecordDetail>, two
  // hosts — see RecordDetailProps.
  const [openRecord, setOpenRecord] = useState<{
    source: string;
    id: string | number;
  } | null>(null);
  // Debounced so a search box does not fire a request per keystroke.
  const [searchTerm, setSearchTerm] = useState('');
  // Stable ids for the tablist wiring and the filter disclosure. useId rather
  // than a literal: two feeds can share a screen (the desk glance and a
  // record's related list), and aria-controls must name exactly one element.
  const uid = useId();
  const panelId = `${uid}-panel`;
  const filtersId = `${uid}-filters`;
  const tabIdFor = (key: string) => `${uid}-tab-${key}`;

  const query: ActivityQuery = useMemo(
    () => ({
      buildingId,
      limit,
      group: group === 'all' ? undefined : [group],
      unit: unit || undefined,
      person: person || undefined,
      status: (status as 'open' | 'closed') || undefined,
      direction: (direction as 'in' | 'out') || undefined,
      day: day || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      q: searchTerm || undefined,
    }),
    [
      buildingId,
      limit,
      group,
      unit,
      person,
      status,
      direction,
      day,
      dateFrom,
      dateTo,
      searchTerm,
    ]
  );

  // Any narrowing at all, so the reset control appears only when it has
  // something to reset. The GROUP TAB is deliberately not counted: it is
  // navigation rather than a filter, it is always visible, and clearing it from
  // a button inside a panel the tab row sits above would move the thing the
  // reader is looking at.
  const anyFilter = Boolean(
    unit || person || status || direction || day || dateFrom || dateTo || search
  );

  // The header's clear-all must not burn on arrival. Surfaces that open with a
  // seeded window (defaultDays) have a non-empty dateFrom BY DEFAULT, and a
  // reset control lit before the reader narrowed anything reads as a bug. The
  // seeded date counts as narrowing only once the reader moves it.
  const seededFrom = useMemo(
    () => (defaultDays ? addDays(todayIn(clock.zone), -defaultDays) : ''),
    [defaultDays, clock.zone]
  );
  const readerNarrowed = Boolean(
    unit ||
    person ||
    status ||
    direction ||
    day ||
    dateTo ||
    search ||
    (dateFrom && dateFrom !== seededFrom)
  );

  /**
   * THE TILE STRIP, AS ONE FOUR-WAY CHOICE.
   *
   * `status` and `direction` are two different query fields, and letting both
   * be on at once produces states nobody pressed for — "still inside AND going
   * out" is empty by definition, and an empty feed that the reader did not ask
   * for is the "0 on everything" report all over again. Selecting one clears
   * the other; selecting the one already on clears it.
   *
   * It deliberately does NOT touch the group tab or the search box. Those are a
   * different question ("which subject", "which words"), they stay legible
   * beside this, and clearing something the reader set elsewhere from a control
   * they pressed here is how a filter row becomes untrustworthy.
   */
  const toggleStat = (pick: 'open' | 'in' | 'out' | 'all') => {
    if (pick === 'all') {
      setStatus('');
      setDirection('');

      return;
    }
    if (pick === 'open') {
      setDirection('');
      setStatus(status === 'open' ? '' : 'open');

      return;
    }
    setStatus('');
    setDirection(direction === pick ? '' : pick);
  };

  const clearFilters = () => {
    setUnit('');
    setPerson('');
    setStatus('');
    setDirection('');
    setDay('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    // The page too: a reader who clears a filter wants the top of the list, and
    // holding a grown limit means a hundred rows arrive where twenty-five were
    // asked for.
    setLimit(compact ? 8 : PAGE);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchActivity(query));
    } catch {
      // Deliberately not the axios message: a failed request must not render as
      // "no activity", which is the RC-10 class of bug where an error looked
      // exactly like an empty day.
      setError('We could not load activity just now.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * IT RE-READS WHEN THE DESK COMES BACK TO IT.
   *
   * `load` ran only when `query` changed: no interval, no focus listener, no
   * socket subscription and no refresh control — on a screen whose entire job is
   * to show what just happened, in a product that already holds a live
   * notification socket open. A concierge who left the tab open through a
   * handover was reading the building's state from whenever they last touched a
   * filter.
   *
   * A focus/visibility re-read rather than a poll: the desk's tab is open all
   * day, and a timer would query three log tables forever for a screen nobody is
   * looking at. Coming back to the tab is the moment the answer matters and the
   * moment the reader will believe it.
   */
  useEffect(() => {
    const reread = () => {
      if (document.visibilityState === 'visible') void load();
    };

    window.addEventListener('focus', reread);
    document.addEventListener('visibilitychange', reread);

    return () => {
      window.removeEventListener('focus', reread);
      document.removeEventListener('visibilitychange', reread);
    };
  }, [load]);

  // PAGING RESETS WHEN THE QUESTION CHANGES. Without this, narrowing a feed you
  // had already pressed "Show more" on asks the server for 75 rows of a result
  // that has 6 — and widening it again silently keeps a page size the reader
  // never chose. The group tab is included: it is the most-pressed control here.
  useEffect(() => {
    setLimit(compact ? 8 : PAGE);
  }, [
    compact,
    group,
    unit,
    person,
    status,
    direction,
    day,
    dateFrom,
    dateTo,
    searchTerm,
  ]);

  // 300ms after typing stops. Without this every keystroke is a query against
  // three log tables.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(search.trim()), 300);

    return () => clearTimeout(t);
  }, [search]);

  // ---- THE CLOCK THE TIMES ARE RENDERED ON ----
  //
  // A desk day is a fact about the BUILDING (Q-7/Q-9, src/desk-day.js) and the
  // server cuts it there for everybody. What a reader SEES it on follows who
  // they are (R-T12): the building's clock at the desk, their own device's on
  // an owner's or a manager's screen.
  //
  // WHICH CLOCK: see `clock` above. At the desk it is the building's own zone
  // whatever the server answered; elsewhere it is the response's `tz`. The
  // clock is NAMED in the footer either way, so nobody is left to assume it is
  // theirs.
  const tz = clock.zone;

  // ONE ROW RENDERER, and it lives in activity-entry-row.tsx now: the shift
  // handover’s full log renders the same row, and two copies of this JSX is how
  // the admin view, the desk view and the handover start disagreeing about what
  // a row shows.
  const renderRow = (entry: ActivityEntry, i: number) => (
    <ActivityEntryRow
      key={`${entry.ref.source}:${entry.ref.id}:${entry.direction}:${i}`}
      entry={entry}
      tz={tz}
      index={i}
      pageSize={pageSize}
      /* A caller may intercept; otherwise it RAISES THE RECORD OVER THIS
         LIST. The ref carries {source, id} — never the serialized v:24 token. */
      onOpen={e =>
        onOpen ? onOpen(e) : setOpenRecord({ source: e.ref.source, id: e.ref.id })
      }
    />
  );

  const counts = data?.groups || [];
  const total = data?.total ?? 0;
  // The tab order, for the arrow keys: All first, then the server's groups.
  const tabKeys: (ActivityGroup | 'all')[] = ['all', ...counts.map(c => c.key)];
  /* ROVING TABINDEX, as role="tablist" promises (and as chrome/tab-bar.tsx
     already does): one tab stop for the strip, arrows to move within it. */
  const onTabKeyDown = (event: React.KeyboardEvent) => {
    const index = tabKeys.indexOf(group);
    if (index < 0) return;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabKeys.length;
    else if (event.key === 'ArrowLeft')
      next = (index - 1 + tabKeys.length) % tabKeys.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabKeys.length - 1;
    else return;
    event.preventDefault();
    setGroup(tabKeys[next]);
    document.getElementById(tabIdFor(tabKeys[next]))?.focus();
  };
  const renderTab = (key: ActivityGroup | 'all', label: React.ReactNode) => {
    const active = group === key;

    return (
      <Tab
        key={key}
        type="button"
        role="tab"
        id={tabIdFor(key)}
        aria-selected={active}
        aria-controls={panelId}
        tabIndex={active ? 0 : -1}
        active={active}
        onClick={() => setGroup(key)}
      >
        {/* The pill lives in the ACTIVE tab only; framer moves it there. */}
        {active ? <TabPill id="activity-groups" /> : null}
        <TabLabel>{label}</TabLabel>
      </Tab>
    );
  };
  const pageSize = compact ? 8 : PAGE;
  const entries = data?.entries ?? [];
  /* ONE STATE KEY for the box below. `loading && data` is a refetch and stays
     'ready' (the rows dim instead of leaving); only a first load with nothing
     to show is 'loading'. An error clears data, so it wins first. */
  const feedState = error
    ? 'error'
    : !data
      ? 'loading'
      : entries.length === 0
        ? 'empty'
        : 'ready';
  // ---- A CONTROL'S LABEL IS COUNTED WITH THAT CONTROL LIFTED ----
  //
  // `total` answers to every filter, tab included, so it is the right
  // denominator for "showing 25 of N" and the WRONG number for the All tab: it
  // shrank to the selected tab's count while the other four tabs read 0, and
  // the strip stopped adding up. `groupTotal` and `moveTotal` are the server's
  // same-vantage-point figures (src/activity/feed.js). Fall back to `total` so
  // an older server still renders something truthful rather than 0.
  const groupTotal = data?.groupTotal ?? total;
  const moveTotal = data?.moveTotal ?? total;

  return (
    <Panel compact={compact} aria-busy={loading}>
      <Header>
        <Text mt={0} mb={0} fontSize={16} fontWeight={700} fontFamily={font.family}>
          {title}
          {/* ONLY ON A REFETCH. A first load gets the skeleton rows below,
              so the two never appear together. aria-hidden because the
              panel already carries aria-busy and a screen reader does not
              need telling twice. */}
          {loading && data ? (
            <>
              {' '}
              <Spinner aria-hidden="true" />
            </>
          ) : null}
        </Text>
        {/* Progressive disclosure on BOTH surfaces. The desk gets filters too —
            it is the desk that needs "which unit was that parcel for" — they
            simply start closed so the panel stays a glance. */}
        {/* SEARCH IS NOT BEHIND THE DISCLOSURE, on either surface. Filters
            narrow a list you are already reading; search is how you find one
            thing you already have in mind, and burying it makes the feed feel
            like a wall. It sits INLINE and BOUNDED rather than as a full-width
            bar on its own line — see SearchField.

            It searches every field on a row, not just the note: people, kind,
            group, direction, unit, priority, who logged it and the text
            (src/activity/feed.js). Bounded by exactly the same window and the
            same RLS as the feed — an admin searches their organisation, a
            manager or concierge searches the buildings they hold, and nobody
            can search past that. */}
        <HeaderTools>
          <SearchField
            aria-label="Search activity"
            placeholder="Search anything…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <FilterToggle
            type="button"
            onClick={() => setShowFilters(v => !v)}
            aria-expanded={showFilters}
            aria-controls={filtersId}
          >
            {showFilters ? 'Hide filters' : 'Filters'}
          </FilterToggle>
          {/* The way back must be visible from where the narrowing shows. With
              the drawer closed, a feed narrowed by a tile or a search term had
              no visible undo — the only "Clear filters" lived INSIDE the drawer
              the reader never opened. Rendered here only while the drawer is
              closed, so the control never appears twice. */}
          {readerNarrowed && !showFilters && (
            <ClearFilters type="button" onClick={clearFilters}>
              Clear filters
            </ClearFilters>
          )}
        </HeaderTools>
      </Header>

      {showSummary && data && (
        <Stats role="group" aria-label="Narrow the feed">
          {/* EACH TILE IS THE FILTER IT DESCRIBES.
              Still inside → status=open, In/Out → the direction, Entries →
              everything. `aria-pressed` rather than a second label, so a screen
              reader is told the same thing the lit border tells everybody
              else. */}
          <Stat
            type="button"
            alert={data.openCount > 0}
            $active={status === 'open'}
            aria-pressed={status === 'open'}
            onClick={() => toggleStat('open')}
          >
            <b>{data.openCount}</b>
            <span>Still inside</span>
          </Stat>
          <Stat
            type="button"
            $active={direction === 'in'}
            aria-pressed={direction === 'in'}
            onClick={() => toggleStat('in')}
          >
            <b>{data.inCount}</b>
            <span>In</span>
          </Stat>
          <Stat
            type="button"
            $active={direction === 'out'}
            aria-pressed={direction === 'out'}
            onClick={() => toggleStat('out')}
          >
            <b>{data.outCount}</b>
            <span>Out</span>
          </Stat>
          {/* NOT A TOGGLE. "Entries" is the whole window, so it clears the two
              narrowings the other three apply — it is lit when neither is on,
              which makes the strip read as one four-way choice rather than
              three independent switches. */}
          <Stat
            type="button"
            $active={!status && !direction}
            aria-pressed={!status && !direction}
            onClick={() => toggleStat('all')}
          >
            <b>{data.totalCapped ? `${moveTotal}+` : moveTotal}</b>
            <span>Entries</span>
          </Stat>
        </Stats>
      )}

      {/* ONE PILL THAT MOVES. IndicatorGroup scopes the layoutId to this feed
          so two feeds on one screen do not trade a pill between them. */}
      <IndicatorGroup id={`${uid}-groups`}>
        <Tabs role="tablist" aria-label="Activity groups" onKeyDown={onTabKeyDown}>
          {renderTab(
            'all',
            <>
              All
              {data ? ` (${data.totalCapped ? `${groupTotal}+` : groupTotal})` : ''}
            </>
          )}
          {counts.map(c =>
            renderTab(
              c.key,
              /* A FLOOR IS ONLY A FLOOR ABOVE ZERO. `totalCapped` says the scan
                 stopped early, so every count is "at least this many" — but
                 "Parcels (0+)" reads as a broken number rather than as a hedge,
                 and an empty book is the one count the cap cannot have
                 truncated downward from. */
              <>
                {c.label} ({c.count}
                {data?.totalCapped && c.count > 0 ? '+' : ''})
              </>
            )
          )}
        </Tabs>
      </IndicatorGroup>

      {/* The disclosure UNFOLDS rather than popping: Collapse animates the
          height open and closed, and keeps the id the toggle's aria-controls
          names. Closed, nothing is rendered — the fields are not in the tree,
          which is what "hidden" should mean to a screen reader too. */}
      <Collapse open={showFilters} id={filtersId}>
        <Filters>
          <FilterField>
            <span>Unit</span>
            <Field
              aria-label="Unit"
              placeholder="Any unit"
              value={unit}
              onChange={e => setUnit(e.target.value)}
            />
          </FilterField>
          <FilterField>
            <span>Person</span>
            <Field
              aria-label="Person"
              placeholder="Any person"
              value={person}
              onChange={e => setPerson(e.target.value)}
            />
          </FilterField>
          {/* THE RANGE. Both ends inclusive and both cut as whole desk days in
              the building's zone by the server, so picking the 1st and the 3rd
              means all three days — see ActivityQuery.dateFrom. Either end
              alone works: "since the 1st" runs to the end of today. Every other
              control on this row still applies inside whatever window these
              two describe. */}
          <FilterField>
            <span>From</span>
            <Field
              aria-label="From date"
              type="date"
              max={dateTo || undefined}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </FilterField>
          <FilterField>
            <span>To</span>
            <Field
              aria-label="To date"
              type="date"
              min={dateFrom || undefined}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </FilterField>
          <FilterField>
            <span>Direction</span>
            <Select
              aria-label="Direction"
              value={direction}
              onChange={e => setDirection(e.target.value)}
            >
              <option value="">In and out</option>
              <option value="in">In</option>
              <option value="out">Out</option>
            </Select>
          </FilterField>
          <FilterField>
            <span>Status</span>
            <Select
              aria-label="Status"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="">Any status</option>
              <option value="open">Still inside</option>
              <option value="closed">Departed</option>
            </Select>
          </FilterField>
          {/* One control that undoes all of them. A feed narrowed by four
              filters and a search term has no other way back, and "reload the
              page" is the answer people actually resort to. */}
          {anyFilter && (
            <ClearFilters type="button" onClick={clearFilters}>
              Clear filters
            </ClearFilters>
          )}
          {/* Plan 3 F20 (b): the dates above are DESK days. Between midnight
              and 07:00 that is not the calendar day, and analytics (which cuts
              at midnight) can show an entry under the next date — so say it. */}
          <DayNote data-desk-day-note="">{DESK_DAY_NOTE}</DayNote>
        </Filters>
      </Collapse>

      {/* THE BOX THE TABS CONTROL. One tabpanel, four fills, crossfaded in
          place (mode wait): the skeleton on a first load, the error, the empty
          line, or the list with its footer. The floor keeps the panel from
          collapsing under a one-line empty state and jumping back when rows
          land. */}
      <div role="tabpanel" id={panelId} aria-labelledby={tabIdFor(group)} tabIndex={-1}>
        <FeedSwitch state={feedState} minHeight={compact ? 96 : 200}>
          {feedState === 'error' ? (
            <Empty role="alert">{error}</Empty>
          ) : feedState === 'loading' ? (
            /* FIRST LOAD: rows shaped like the content, so nothing jumps when
               the real ones land. Never together with the spinner above --
               that one is for a refetch, where real rows are already up. */
            <List aria-hidden="true">
              {[0, 1, 2, 3, 4].map(i => (
                <SkeletonRow key={i}>
                  <SkeletonBar $w="40px" />
                  <SkeletonBar $w={['70%', '55%', '80%', '45%', '65%'][i]} />
                  <SkeletonBar $w="32px" />
                </SkeletonRow>
              ))}
            </List>
          ) : feedState === 'empty' ? (
            <Empty>
              {group === 'all'
                ? 'Nothing has been logged in this window yet.'
                : 'Nothing in this group for the selected window.'}
            </Empty>
          ) : (
            <>
              {/* ONE TIMELINE, newest first, whatever the tab. The tabs and
                  the filters are what produce a group; the list itself never
                  rearranges the sequence of a shift into separate books. */}
              <Staleable stale={loading}>
                <List>{entries.map(renderRow)}</List>
              </Staleable>

              {/* HOW FAR THROUGH, AND WHETHER THERE IS MORE. `totalCapped`
                  means the server stopped counting at its scan ceiling, so the
                  total is a FLOOR and is rendered as "118+" rather than as a
                  confident figure. */}
              <Footer>
                {data?.hasMore && (
                  <More type="button" onClick={() => setLimit(l => l + pageSize)}>
                    Show more
                  </More>
                )}
                <PageNote>
                  {data?.hasMore
                    ? `Showing ${entries.length} of ${
                        data.totalCapped ? `${total}+` : total
                      }`
                    : `All ${total}${data?.totalCapped ? '+' : ''} shown`}
                  {/* ---- WHICH CLOCK THE TIMES ARE ON, SAID OUT LOUD ----
                      "Building time — Toronto" at the desk; "Your time —
                      Budapest" for an owner or a manager elsewhere. A time
                      with no clock named beside it reads as the reader's own,
                      and for half the people looking at this panel it is not. */}
                  {' · '}
                  <ClockNote clock={clock} inline />
                </PageNote>
              </Footer>
            </>
          )}
        </FeedSwitch>
      </div>
      {/* THE RECORD, RAISED OVER THE LIST. FormModal is the shared layer — it
          portals out of the dashboard's transformed pane, traps focus, closes on
          Escape and on a scrim press, and becomes a bottom sheet under the small
          breakpoint, so this is responsive without a second set of rules here.
          The feed underneath keeps its filters, its search and its page. */}
      {openRecord && (
        <FormModal wide title="Record" onClose={() => setOpenRecord(null)}>
          <RecordDetail
            source={openRecord.source}
            id={String(openRecord.id)}
            onClose={() => setOpenRecord(null)}
            /* An out taken from the record (sign out, close, hand over) is a
               new row in this feed — re-read it so it is there when the
               record closes. */
            onChanged={() => void load()}
          />
        </FormModal>
      )}
    </Panel>
  );
};
